import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Star, Sparkles, ArrowRight, CheckCircle2, Package,
  Leaf, Shield, FlaskConical, Heart, Fish, Drumstick, PawPrint, Bone,
  Activity, Salad, Droplet, Baby, Dog,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same icon-derivation logic as the Live Show screen so the public page feels consistent
const deriveBenefitIcons = (product = {}) => {
  const icons = [];
  const seen = new Set();
  const add = (key, Icon, label) => {
    if (seen.has(key)) return;
    seen.add(key);
    icons.push({ key, Icon, label });
  };

  const type = (product.type || '').toLowerCase();
  if (type === 'grain_free') add('grain_free', Leaf, 'Grain Free');
  else if (type === 'hydrolysed') add('hydrolysed', FlaskConical, 'Hydrolysed');
  else if (type === 'superfoods') add('superfoods', Salad, 'Superfoods');

  const lifestage = (product.lifestage || '').toLowerCase();
  if (lifestage === 'puppy') add('puppy', Baby, 'Puppy Recipe');
  else if (lifestage === 'senior') add('senior', Heart, 'Senior Recipe');
  else if (lifestage === 'adult') add('adult', Dog, 'Adult Recipe');

  const breed = (product.breed_size || '').toLowerCase();
  if (breed === 'small') add('small', PawPrint, 'Small Breed');
  else if (breed === 'large') add('large', Dog, 'Large Breed');

  const blob = `${(product.tags || []).join(' ')} ${product.benefits || ''} ${product.ingredients || ''}`.toLowerCase();
  if (/fish|salmon|trout/.test(blob)) add('fish', Fish, 'Fresh Fish');
  else if (/chicken|lamb|turkey|duck|beef/.test(blob)) add('meat', Drumstick, 'Fresh Meat');
  if (/joint|hip|mobility|glucosamine|chondroitin/.test(blob)) add('joint', Bone, 'Joint Support');
  if (/digestive|gut|sensitive|prebiotic|fos|mos/.test(blob)) add('digestive', Shield, 'Digestive Care');
  if (/coat|skin|omega/.test(blob)) add('coat', Droplet, 'Skin & Coat');
  if (/energy|active|protein/.test(blob)) add('active', Activity, 'Active Dogs');
  if (/natural|no artificial|preservation/.test(blob)) add('natural', Leaf, 'All Natural');

  return icons.slice(0, 3);
};

export const PublicVsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/vs/${slug}`)
      .then(res => { if (!cancelled) { setData(res.data); setLoading(false); } })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.status === 404 ? 'Page not found' : 'Could not load this page');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  // SEO meta + title
  useEffect(() => {
    if (!data) return;
    const bag = data.scanned_bag || {};
    const brand = (bag.brand || '').trim();
    const product = (bag.product_name || '').trim();
    const fullTitle = `${brand} ${product} vs Ultimate Pet Foods — Honest Comparison`.trim();
    document.title = fullTitle;
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const desc = `See how ${brand} ${product} compares to Ultimate Pet Foods. Real ingredients, real analysis, real alternatives — direct from Ted AI.`;
    setMeta('description', desc);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'article', true);
    if (data.front_image_url) {
      setMeta('og:image', `${process.env.REACT_APP_BACKEND_URL}${data.front_image_url}`, true);
    }
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);

    // FAQ schema (JSON-LD) for AI Overviews / rich results
    const faqs = data.comparison?.faqs || [];
    let ld = document.getElementById('public-vs-faq-jsonld');
    if (faqs.length > 0) {
      const payload = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      };
      if (!ld) {
        ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'public-vs-faq-jsonld';
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify(payload);
    } else if (ld) {
      ld.remove();
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0FA49C]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-gray-50">
        <Package className="w-12 h-12 text-gray-300 mb-3" />
        <h1 className="text-xl font-bold text-[#222] mb-1">{error}</h1>
        <p className="text-sm text-[#666] mb-5">This comparison may have been removed.</p>
        <button
          onClick={() => navigate('/')}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white px-6 py-2.5 rounded-full font-semibold text-sm"
          data-testid="public-vs-home-btn"
        >
          Visit Ultimate Pet Foods
        </button>
      </div>
    );
  }

  const bag = data.scanned_bag || {};
  const matches = data.recommended_products || [];
  const topMatch = matches[0];
  const cmp = data.comparison || {};
  const competitorName = `${bag.brand || ''} ${bag.product_name || ''}`.trim() || 'this bag';
  const upfName = topMatch?.product?.name || 'Ultimate Pet Foods';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-vs-page">
      {/* Hero */}
      <header
        className="relative overflow-hidden text-white"
        style={{
          backgroundColor: '#1f6d62',
          backgroundImage: `
            radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.12) 0%, transparent 45%),
            radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%)
          `,
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <button
            onClick={() => navigate('/')}
            className="text-xs text-white/70 hover:text-white mb-6 uppercase tracking-widest"
            data-testid="public-vs-back-home"
          >
            ← Ultimate Pet Foods
          </button>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-4">
            <Sparkles className="w-3 h-3" /> Ted AI Comparison
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95]">
            {bag.brand} {bag.product_name}
            <br />
            <span className="text-[#a0da39]">vs Ultimate Pet Foods</span>
          </h1>
          <p
            className="mt-4 text-xl sm:text-2xl italic text-[#a0da39]"
            style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
          >
            An honest, AI-powered comparison.
          </p>
        </div>
      </header>

      {/* Who this is for */}
      {cmp.who_for && (
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
            <p className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-2">Who this comparison is for</p>
            <p className="text-base text-[#222] leading-relaxed" data-testid="public-vs-who-for">{cmp.who_for}</p>
          </div>
        </section>
      )}

      {/* Bag detail */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <h2 className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-3">The Bag We Scanned</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
          {data.front_image_url && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${data.front_image_url}`}
              alt={`${bag.brand} ${bag.product_name}`}
              className="w-full h-64 sm:h-80 object-cover rounded-xl border border-gray-200"
            />
          )}
          <div className="space-y-4">
            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Brand</p>
              <p className="text-2xl font-extrabold text-[#222]">{bag.brand || 'Unknown'}</p>
              <p className="text-sm text-[#555] mt-0.5">{bag.product_name}</p>
            </div>
            {bag.protein_source && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Protein</p>
                <p className="text-sm text-[#333] capitalize">{bag.protein_source}</p>
              </div>
            )}
            {bag.type && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Recipe Type</p>
                <p className="text-sm text-[#333] capitalize">{bag.type.replace(/_/g, ' ')}</p>
              </div>
            )}
            {bag.ingredients && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Ingredients</p>
                <p className="text-xs text-[#444] leading-relaxed">{bag.ingredients}</p>
              </div>
            )}
            {bag.analytical_constituents && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Nutrition</p>
                <p className="text-xs text-[#444]">{bag.analytical_constituents}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Verdict */}
      {cmp.verdict && (
        <section className="bg-[#f4f7ea] border-y-2 border-[#a0da39]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
            <p className="text-xs uppercase tracking-widest text-[#1f6d62] font-bold mb-3">Our Verdict</p>
            <p className="text-base sm:text-lg text-[#1f3d2f] leading-relaxed" data-testid="public-vs-verdict">{cmp.verdict}</p>
          </div>
        </section>
      )}

      {/* Ingredient breakdown */}
      {Array.isArray(cmp.ingredient_analysis) && cmp.ingredient_analysis.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <h2 className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-3">Ingredient Breakdown</h2>
          <p className="text-sm text-[#666] mb-5">A category-by-category look at how the two recipes actually stack up.</p>
          <div className="space-y-3" data-testid="public-vs-ingredients">
            {cmp.ingredient_analysis.map((row, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-sm font-bold text-[#222]">{row.category}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                  <div className="p-4">
                    <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">{competitorName}</p>
                    <p className="text-sm text-[#333] leading-snug">{row.scanned || '—'}</p>
                  </div>
                  <div className="p-4 bg-[#a0da39]/5">
                    <p className="text-[10px] text-[#1f6d62] uppercase tracking-wider font-bold mb-1">{upfName}</p>
                    <p className="text-sm text-[#222] leading-snug font-medium">{row.upf || '—'}</p>
                  </div>
                </div>
                {row.note && (
                  <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
                    <p className="text-xs text-[#555] leading-relaxed"><strong className="text-[#222]">Why this matters:</strong> {row.note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Nutrition table */}
      {Array.isArray(cmp.nutrition_table) && cmp.nutrition_table.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <h2 className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-3">Guaranteed Analysis</h2>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="public-vs-nutrition">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#666] font-bold">Nutrient</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#666] font-bold">{competitorName}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#1f6d62] font-bold bg-[#a0da39]/10">{upfName}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cmp.nutrition_table.map((row, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 font-semibold text-[#222]">{row.label}</td>
                    <td className="px-4 py-3 text-[#444]">{row.scanned || 'Not stated'}</td>
                    <td className="px-4 py-3 text-[#222] font-medium bg-[#a0da39]/5">{row.upf || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cmp.nutrition_table.some(r => r.comment) && (
            <ul className="mt-3 space-y-1 text-xs text-[#666] leading-relaxed">
              {cmp.nutrition_table.filter(r => r.comment).map((r, i) => (
                <li key={i}><strong>{r.label}:</strong> {r.comment}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Matches */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
        <h2 className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-3">
          Top {matches.length} Ultimate Pet Foods Matches
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {matches.map((m, i) => {
            const p = m.product || {};
            const icons = deriveBenefitIcons(p);
            return (
              <motion.div
                key={p.id || i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative rounded-2xl border-2 bg-white overflow-hidden ${
                  i === 0 ? 'border-[#a0da39]' : 'border-gray-200'
                }`}
                data-testid={`public-match-${i + 1}`}
              >
                {i === 0 && (
                  <span className="absolute top-3 left-3 bg-[#a0da39] text-[#1f6d62] text-[10px] font-black uppercase px-2 py-0.5 rounded-full tracking-widest z-10">
                    Top Pick
                  </span>
                )}
                {p.image_url && (
                  <img src={p.image_url} alt={p.name} className="w-full h-48 object-cover" />
                )}
                <div className="p-4 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-extrabold text-[#222] text-sm leading-tight">{p.name || m.product_name}</p>
                    {m.match_score && (
                      <span className="flex items-center gap-0.5 bg-[#1f6d62] text-white px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0">
                        <Star className="w-3 h-3 fill-white" />
                        {m.match_score}%
                      </span>
                    )}
                  </div>
                  {p.type && (
                    <p className="text-[10px] text-[#0FA49C] font-bold uppercase tracking-wider">
                      {p.type.replace(/_/g, ' ')}
                    </p>
                  )}
                  {icons.length > 0 && (
                    <div className="flex gap-1.5">
                      {icons.map(b => (
                        <div
                          key={b.key}
                          className="flex-1 flex flex-col items-center gap-1 bg-gray-50 rounded-lg py-1.5 px-1"
                          title={b.label}
                        >
                          <div className="w-7 h-7 rounded-full bg-[#a0da39]/20 border border-[#a0da39]/40 flex items-center justify-center">
                            <b.Icon className="w-3.5 h-3.5 text-[#1f6d62]" />
                          </div>
                          <span className="text-[8px] font-bold text-[#444] text-center uppercase tracking-wide leading-tight">{b.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.why_selected && (
                    <p className="text-xs text-[#444] leading-relaxed">{m.why_selected}</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FAQs */}
      {Array.isArray(cmp.faqs) && cmp.faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <h2 className="text-xs uppercase tracking-widest text-[#0FA49C] font-bold mb-3">Frequently Asked Questions</h2>
          <div className="space-y-3" data-testid="public-vs-faqs">
            {cmp.faqs.map((f, i) => (
              <details key={i} className="bg-white rounded-xl border border-gray-200 group">
                <summary className="cursor-pointer px-4 py-3 font-bold text-[#222] text-sm flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="text-[#0FA49C] group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <div className="px-4 pb-4 text-sm text-[#444] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
          <CheckCircle2 className="w-10 h-10 text-[#0FA49C] mx-auto mb-3" />
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#222] mb-2">
            Try the top match for just <span className="text-[#0FA49C]">£1</span>
          </h2>
          <p className="text-sm text-[#666] max-w-xl mx-auto mb-6">
            Build a personalised sample box with {topMatch?.product?.name || 'our top match'} and other recipes Ted picked — risk-free.
          </p>
          <button
            onClick={() => navigate('/?tab=sample-box')}
            className="inline-flex items-center gap-2 bg-[#a0da39] hover:bg-[#8ac82f] text-[#1f3d2f] font-extrabold uppercase tracking-wide px-7 py-4 rounded-full text-sm shadow-lg"
            data-testid="public-vs-cta"
          >
            Build My Sample Box <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="text-center py-6 text-xs text-[#999]">
        © Ultimate Pet Foods — Powered by <strong>Ted AI</strong>
      </footer>
    </div>
  );
};

export default PublicVsPage;
